import React from 'react';
import { Routes, Route, Navigate, HashRouter } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AcademyProvider } from './context/AcademyContext';
import SessionGate from './components/SessionGate';
import RunStateControls from './components/RunStateControls';

import AdminLayout from './layouts/AdminLayout';
import CamperLayout from './layouts/CamperLayout';
import AuthHandoff from './pages/auth/AuthHandoff';
import HomePage from './pages/public/HomePage';
import Register from './pages/public/Register';
import Start from './pages/public/Start';

import AdminDashboard from './pages/admin/AdminDashboard';
import Sessions from './pages/admin/Sessions';
import Users from './pages/admin/Users';
import Passes from './pages/admin/Passes';
import Bunkmates from './pages/admin/Bunkmates';
import ContentManager from './pages/admin/ContentManager';
import PodcastManager from './pages/admin/PodcastManager';
import Settings from './pages/admin/Settings';
import LettersQueue from './pages/admin/LettersQueue';

import Departments from './pages/camper/Departments';
import Hub from './pages/camper/Hub';
import Dashboard from './pages/camper/Dashboard';
import Assembly from './pages/camper/Assembly';
import MessHall from './pages/camper/MessHall';
import StudyHall from './pages/camper/StudyHall';
import Campfire from './pages/camper/Campfire';
import Letters from './pages/camper/Letters';
import Bunkmate from './pages/camper/Bunkmate';
import Podcast from './pages/camper/Podcast';
import Profile from './pages/camper/Profile';
import Upgrade from './pages/camper/Upgrade';
import Tools from './pages/camper/Tools';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="min-h-screen bg-cream flex items-center justify-center">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="min-h-screen bg-cream flex items-center justify-center">Loading...</div>;
  }

  const safeUser = Array.isArray(user) ? user[0] : user;

  if (!safeUser || safeUser.role !== 'admin') {
    return <Navigate to="/hub" replace />;
  }

  return children;
};

function App() {
  return (
    <HashRouter>
      <AuthProvider>
        <AcademyProvider>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/register" element={<Register />} />
            <Route path="/start" element={<Start />} />
            <Route path="/login" element={<AuthHandoff />} />

            <Route path="/centers" element={
              <ProtectedRoute>
                <Departments />
              </ProtectedRoute>
            } />
            <Route path="/departments" element={<Navigate to="/centers" replace />} />

            <Route path="/admin" element={
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            }>
              <Route index element={<AdminDashboard />} />
              <Route path="sessions" element={
                <>
                  <RunStateControls />
                  <Sessions />
                </>
              } />
              <Route path="users" element={<Users />} />
              <Route path="passes" element={<Passes />} />
              <Route path="bunkmates" element={<Bunkmates />} />
              <Route path="content" element={<ContentManager />} />
              <Route path="podcast" element={<PodcastManager />} />
              <Route path="letters" element={<LettersQueue />} />
              <Route path="settings" element={<Settings />} />
            </Route>

            <Route path="/hub" element={
              <ProtectedRoute>
                <Hub />
              </ProtectedRoute>
            } />

            <Route path="/hub/:runSlug" element={
              <ProtectedRoute>
                <SessionGate>
                  <CamperLayout />
                </SessionGate>
              </ProtectedRoute>
            }>
              <Route index element={<Dashboard />} />
              <Route path="lesson" element={<Assembly />} />
              <Route path="lab" element={<MessHall />} />
              <Route path="studyhall" element={<StudyHall />} />
              <Route path="commons" element={<Campfire />} />
              <Route path="tools" element={<Tools />} />
              <Route path="letters" element={<Letters />} />
              <Route path="bunkmate" element={<Bunkmate />} />
              <Route path="podcast" element={<Podcast />} />
              <Route path="profile" element={<Profile />} />
              <Route path="upgrade" element={<Upgrade />} />
            </Route>

            <Route path="/dashboard" element={<Navigate to="/hub" replace />} />
            <Route path="/lesson" element={<Navigate to="/hub" replace />} />
            <Route path="/lab" element={<Navigate to="/hub" replace />} />
            <Route path="/studyhall" element={<Navigate to="/hub" replace />} />
            <Route path="/commons" element={<Navigate to="/hub" replace />} />
            <Route path="/tools" element={<Navigate to="/hub" replace />} />
            <Route path="/letters" element={<Navigate to="/hub" replace />} />
            <Route path="/bunkmate" element={<Navigate to="/hub" replace />} />
            <Route path="/podcast" element={<Navigate to="/hub" replace />} />
            <Route path="/profile" element={<Navigate to="/hub" replace />} />
            <Route path="/upgrade" element={<Navigate to="/hub" replace />} />
          </Routes>
        </AcademyProvider>
      </AuthProvider>
    </HashRouter>
  );
}

export default App;